import * as React from "react";
import type {
  ActivityTransaction,
  ActivityFilters,
  ActivityStats,
  ActivityApiResponse,
} from "@/lib/types/activity";
import { api } from "@/lib/api/client";
import { getUserFriendlyError } from "@/lib/utils/error-messages";
import { logger } from "@/lib/utils/logger";

export interface UseActivityDataOptions {
  defaultPageSize?: number;
  autoLoad?: boolean;
  initialFilters?: Partial<ActivityFilters>;
}

export interface UseActivityDataReturn {
  transactions: ActivityTransaction[];
  stats: ActivityStats | null;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  error: string | null;
  filters: ActivityFilters;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setFilters: (filters: Partial<ActivityFilters>) => void;
  resetFilters: () => void;
  refetch: () => Promise<void>;
}

const DEFAULT_FILTERS: ActivityFilters = {
  startDate: null,
  endDate: null,
  transactionType: null,
  search: "",
};

/**
 * Hook for fetching and managing activity data with pagination, filtering, and stats
 */
export function useActivityData(
  options: UseActivityDataOptions = {}
): UseActivityDataReturn {
  const {
    defaultPageSize = 50,
    autoLoad = true,
    initialFilters = {},
  } = options;

  // State management
  const [transactions, setTransactions] = React.useState<ActivityTransaction[]>(
    []
  );
  const [stats, setStats] = React.useState<ActivityStats | null>(null);
  const [page, setPageState] = React.useState(1);
  const [pageSize, setPageSizeState] = React.useState(defaultPageSize);
  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: defaultPageSize,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = React.useState(autoLoad);
  const [error, setError] = React.useState<string | null>(null);
  const [filters, setFiltersState] = React.useState<ActivityFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = React.useState(filters.search);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      // Reset to page 1 when search changes
      if (filters.search !== debouncedSearch) {
        setPageState(1);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search, debouncedSearch]);

  // Fetch data function
  const fetchData = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("pageSize", pageSize.toString());

      if (filters.startDate) {
        params.append("startDate", filters.startDate);
      }

      if (filters.endDate) {
        params.append("endDate", filters.endDate);
      }

      if (filters.transactionType && filters.transactionType !== "all") {
        params.append("transactionType", filters.transactionType);
      }

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }

      if (filters.userIds) {
        params.append("userIds", filters.userIds);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const responseData: any = await api.get("/activity", {
        params: Object.fromEntries(params),
      });

      const result: ActivityApiResponse = responseData.data || responseData;

      setTransactions(result.transactions);
      setStats(result.stats);
      setPagination(result.pagination);
    } catch (err) {
      const message = getUserFriendlyError(
        err,
        "Failed to fetch activity data"
      );
      setError(message);
      logger.error("Activity data fetch error", err, {
        operation: "fetch_activity_data",
        metadata: { filters, page, pageSize },
      });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    filters.startDate,
    filters.endDate,
    filters.transactionType,
    filters.userIds,
    debouncedSearch,
  ]);

  // Fetch data when dependencies change
  React.useEffect(() => {
    if (autoLoad) {
      fetchData();
    }
  }, [fetchData, autoLoad]);

  // Control functions
  const setPage = React.useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = React.useCallback((newPageSize: number) => {
    setPageSizeState(newPageSize);
    setPageState(1); // Reset to page 1 when page size changes
  }, []);

  const setFilters = React.useCallback(
    (newFilters: Partial<ActivityFilters>) => {
      setFiltersState((prev) => ({ ...prev, ...newFilters }));
      // Reset to page 1 when filters change
      setPageState(1);
    },
    []
  );

  const resetFilters = React.useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
    setPageState(1);
  }, []);

  const refetch = React.useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return {
    transactions,
    stats,
    pagination,
    isLoading,
    error,
    filters,
    setPage,
    setPageSize,
    setFilters,
    resetFilters,
    refetch,
  };
}
